import React, { useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
    Box,
    Drawer,
    AppBar,
    Toolbar,
    List,
    Typography,
    Divider,
    IconButton,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useTheme,
    useMediaQuery,
    Avatar,
    Tooltip,
    alpha,
    Badge,
    Collapse,
    Switch,
    Chip,
    Menu,
    MenuItem,
    Button,
    InputBase,
} from '@mui/material';
import {
    Menu as MenuIcon,
    Dashboard as DashboardIcon,
    School as SchoolIcon,
    Person as PersonIcon,
    Settings as SettingsIcon,
    ChevronLeft as ChevronLeftIcon,
    Notifications as NotificationsIcon,
    Logout as LogoutIcon,
    ExpandLess as ExpandLessIcon,
    ExpandMore as ExpandMoreIcon,
    DarkMode as DarkModeIcon,
    LightMode as LightModeIcon,
    Group as GroupIcon,
    Assignment as AssignmentIcon,
    CalendarMonth as CalendarIcon,
    SpaceDashboard as SpaceDashboardIcon,
    AccountBalance as AccountBalanceIcon,
    PeopleAlt as PeopleAltIcon,
    TaskAlt as TaskAltIcon,
    EventNote as EventNoteIcon,
    AccountCircle as AccountCircleIcon,
    Tune as TuneIcon,
    NotificationsActive as NotificationsActiveIcon,
    PowerSettingsNew as PowerSettingsNewIcon,
    DashboardCustomize as DashboardCustomizeIcon,
    AccountBalanceWallet as AccountBalanceWalletIcon,
    Groups as GroupsIcon,
    AssignmentOutlined as AssignmentOutlinedIcon,
    CalendarToday as CalendarTodayIcon,
    PersonOutline as PersonOutlineIcon,
    SettingsOutlined as SettingsOutlinedIcon,
    NotificationsNone as NotificationsNoneIcon,
    PowerSettingsNewOutlined as PowerSettingsNewOutlinedIcon,
    SchoolOutlined as SchoolOutlinedIcon,
    MenuBookOutlined as MenuBookOutlinedIcon,
    EventAvailableOutlined as EventAvailableOutlinedIcon,
    AccountCircleOutlined as AccountCircleOutlinedIcon,
    TuneOutlined as TuneOutlinedIcon,
    ViewQuilt as ViewQuiltIcon,
    Business as BusinessIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Description as DescriptionIcon,
    Event as EventIcon,
    Badge as BadgeIcon,
    SettingsApplications as SettingsApplicationsIcon,
    MenuBook as MenuBookIcon,
    EventAvailable as EventAvailableIcon,
    Search as SearchIcon,
    HelpOutline as HelpIcon,
    Language as LanguageIcon,
    KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useColorScheme } from '@mui/material/styles';

const drawerWidth = 280;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
    open?: boolean;
}>(({ theme, open }) => ({
    flexGrow: 1,
    padding: theme.spacing(3),
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    width: '100%',
    ...(open && {
        marginLeft: 0,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.easeOut,
            duration: theme.transitions.duration.enteringScreen,
        }),
    }),
    backgroundColor: theme.palette.background.default,
    minHeight: '100vh',
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: drawerWidth,
        boxSizing: 'border-box',
        border: 'none',
        background: theme.palette.mode === 'dark' 
            ? theme.palette.grey[900]
            : theme.palette.common.white,
        boxShadow: theme.palette.mode === 'dark'
            ? 'none'
            : '0 0 0 1px rgba(0, 0, 0, 0.05), 0 2px 4px rgba(0, 0, 0, 0.05)',
    },
}));

const StyledAppBar = styled(AppBar)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark' 
        ? alpha(theme.palette.background.paper, 0.8)
        : alpha(theme.palette.background.paper, 0.9),
    backdropFilter: 'blur(10px)',
    boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.05)}`,
    borderBottom: `1px solid ${theme.palette.mode === 'dark' 
        ? alpha(theme.palette.common.white, 0.1)
        : alpha(theme.palette.common.black, 0.05)}`,
    transition: theme.transitions.create(['margin', 'width'], {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(0, 2),
    minHeight: 70,
    gap: theme.spacing(2),
}));

const MenuButton = styled(IconButton)(({ theme }) => ({
    marginRight: theme.spacing(2),
    color: theme.palette.primary.main,
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
}));

const SidebarHeader = styled(Box)(({ theme }) => ({
    height: 80,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(0, 2),
    borderBottom: `1px solid ${theme.palette.mode === 'dark' 
        ? theme.palette.grey[800] 
        : theme.palette.grey[100]}`,
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -1,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '40%',
        height: '2px',
        background: theme.palette.primary.main,
        borderRadius: '2px',
    },
}));

const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `radial-gradient(circle at top right, 
            ${alpha(theme.palette.primary.main, 0.1)}, 
            transparent 70%)`,
        borderRadius: theme.spacing(1),
        opacity: 0,
        transition: 'opacity 0.2s ease',
    },
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.grey[50],
        '&::before': {
            opacity: 1,
        },
    },
    '& .MuiSvgIcon-root': {
        fontSize: 32,
        color: theme.palette.primary.main,
        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
    },
}));

const MenuSection = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2, 0),
    borderBottom: `1px solid ${theme.palette.mode === 'dark' 
        ? theme.palette.grey[800] 
        : theme.palette.grey[100]}`,
    position: 'relative',
    '&::after': {
        content: '""',
        position: 'absolute',
        bottom: -1,
        left: '50%',
        transform: 'translateX(-50%)',
        width: '30%',
        height: '1px',
        background: theme.palette.primary.main,
        opacity: 0.5,
        borderRadius: '1px',
    },
}));

const MenuGroupTitle = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1, 2),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '& .MuiTypography-root': {
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '1px',
        color: theme.palette.mode === 'dark'
            ? theme.palette.grey[500]
            : theme.palette.grey[600],
    },
    '& .MuiChip-root': {
        height: 20,
        fontSize: '0.65rem',
        fontWeight: 600,
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
        color: theme.palette.primary.main,
        '& .MuiChip-label': {
            padding: '0 8px',
        },
    },
}));

const NavItem = styled(ListItemButton)(({ theme }) => ({
    margin: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    transition: 'all 0.2s ease',
    position: 'relative',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `radial-gradient(circle at center, 
            ${alpha(theme.palette.primary.main, 0.08)}, 
            transparent 70%)`,
        borderRadius: theme.spacing(1),
        opacity: 0,
        transition: 'opacity 0.2s ease',
    },
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.grey[50],
        '&::before': {
            opacity: 1,
        },
    },
    '&.Mui-selected': {
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.primary.main, 0.15)
            : alpha(theme.palette.primary.main, 0.08),
        '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: '60%',
            backgroundColor: theme.palette.primary.main,
            borderRadius: '0 2px 2px 0',
            boxShadow: `0 0 8px ${alpha(theme.palette.primary.main, 0.5)}`,
        },
        '&:hover': {
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.2)
                : alpha(theme.palette.primary.main, 0.12),
        },
    },
}));

const NavIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: 40,
    color: 'inherit',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
        transition: 'all 0.2s ease',
        padding: theme.spacing(0.5),
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.05)
            : alpha(theme.palette.primary.main, 0.05),
        '&.dashboard-icon': {
            transform: 'rotate(-10deg)',
        },
        '&.school-icon': {
            transform: 'scale(1.1)',
        },
        '&.students-icon': {
            transform: 'translateY(-1px)',
        },
        '&.assignments-icon': {
            transform: 'rotate(5deg)',
        },
        '&.calendar-icon': {
            transform: 'scale(1.05)',
        },
    },
    '.Mui-selected &': {
        color: theme.palette.primary.main,
        '& .MuiSvgIcon-root': {
            transform: 'scale(1.1)',
            backgroundColor: theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.15)
                : alpha(theme.palette.primary.main, 0.1),
            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
            '&.dashboard-icon': {
                transform: 'rotate(-10deg) scale(1.1)',
            },
            '&.school-icon': {
                transform: 'scale(1.2)',
            },
            '&.students-icon': {
                transform: 'translateY(-1px) scale(1.1)',
            },
            '&.assignments-icon': {
                transform: 'rotate(5deg) scale(1.1)',
            },
            '&.calendar-icon': {
                transform: 'scale(1.15)',
            },
        },
    },
}));

const NavText = styled(ListItemText)(({ theme }) => ({
    '& .MuiTypography-root': {
        fontSize: '0.875rem',
        fontWeight: 500,
        color: theme.palette.mode === 'dark'
            ? theme.palette.grey[300]
            : theme.palette.grey[800],
    },
    '.Mui-selected & .MuiTypography-root': {
        color: theme.palette.primary.main,
        fontWeight: 600,
    },
}));

const UserSection = styled(Box)(({ theme }) => ({
    marginTop: 'auto',
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.mode === 'dark' 
        ? theme.palette.grey[800] 
        : theme.palette.grey[100]}`,
}));

const UserProfile = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? theme.palette.grey[800]
            : theme.palette.grey[50],
    },
}));

const UserAvatar = styled(Avatar)(({ theme }) => ({
    width: 40,
    height: 40,
    background: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: '1rem',
    fontWeight: 600,
}));

const NotificationBadge = styled(Badge)(({ theme }) => ({
    '& .MuiBadge-badge': {
        backgroundColor: theme.palette.error.main,
        boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    },
}));

const ThemeSwitch = styled(Switch)(({ theme }) => ({
    width: 62,
    height: 34,
    padding: 7,
    '& .MuiSwitch-switchBase': {
        margin: 1,
        padding: 0,
        transform: 'translateX(6px)',
        '&.Mui-checked': {
            color: '#fff',
            transform: 'translateX(22px)',
            '& + .MuiSwitch-track': {
                opacity: 1,
                background: theme.palette.mode === 'dark'
                    ? 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)'
                    : 'linear-gradient(45deg, #FF9800 30%, #FFC107 90%)',
                boxShadow: theme.palette.mode === 'dark'
                    ? '0 0 10px rgba(33, 150, 243, 0.5)'
                    : '0 0 10px rgba(255, 152, 0, 0.5)',
            },
        },
    },
    '& .MuiSwitch-thumb': {
        backgroundColor: theme.palette.mode === 'dark' 
            ? theme.palette.grey[400]
            : theme.palette.grey[300],
        width: 32,
        height: 32,
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:before': {
            content: "''",
            position: 'absolute',
            width: '100%',
            height: '100%',
            left: 0,
            top: 0,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
            backgroundImage: theme.palette.mode === 'dark'
                ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
                    theme.palette.common.white,
                )}" d="M4.2 2.5l-.7 1.8-1.8.7 1.8.7.7 1.8.6-1.8L6.7 5l-1.9-.7-.6-1.8zm15 8.3a6.7 6.7 0 11-6.6-6.6 5.8 5.8 0 006.6 6.6z"/></svg>')`
                : `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 20 20"><path fill="${encodeURIComponent(
                    theme.palette.common.white,
                )}" d="M9.305 1.667V3.75h1.389V1.667h-1.39zm-4.707 1.95l-.982.982L5.09 6.072l.982-.982-1.473-1.473zm10.802 0L13.927 5.09l.982.982 1.473-1.473-.982-.982zM10 5.139a4.872 4.872 0 00-4.862 4.86A4.872 4.872 0 0010 14.862 4.872 4.872 0 0014.86 10 4.872 4.872 0 0010 5.139zm0 1.389A3.462 3.462 0 0113.471 10a3.462 3.462 0 01-3.473 3.472A3.462 3.462 0 016.527 10 3.462 3.462 0 0110 6.528zM1.665 9.305v1.39h2.083v-1.39H1.666zm14.583 0v1.39h2.084v-1.39h-2.084zM5.09 13.928L3.616 15.4l.982.982 1.473-1.473-.982-.982zm9.82 0l-.982.982 1.473 1.473.982-.982-1.473-1.473zM9.305 16.25v2.083h1.389V16.25h-1.39z"/></svg>')`,
        },
    },
    '& .MuiSwitch-track': {
        opacity: 1,
        backgroundColor: theme.palette.mode === 'dark' 
            ? alpha(theme.palette.grey[700], 0.8)
            : alpha(theme.palette.grey[200], 0.8),
        borderRadius: 20 / 2,
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    },
    '&:hover .MuiSwitch-thumb': {
        boxShadow: theme.palette.mode === 'dark'
            ? '0 0 8px rgba(33, 150, 243, 0.5)'
            : '0 0 8px rgba(255, 152, 0, 0.5)',
    },
}));

const LogoIcon = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 40,
    height: 40,
    borderRadius: theme.spacing(1.2),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
    color: theme.palette.common.white,
    '& .MuiSvgIcon-root': {
        fontSize: '1.75rem',
        transform: 'rotate(-5deg)',
    },
    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
}));

const SearchBar = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    backgroundColor: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.05)
        : alpha(theme.palette.common.black, 0.03),
    borderRadius: theme.spacing(2),
    padding: theme.spacing(0.5, 1.5),
    width: '100%',
    maxWidth: 400,
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.08)
            : alpha(theme.palette.common.black, 0.05),
    },
    '& .MuiInputBase-root': {
        width: '100%',
    },
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1, 1, 0),
        fontSize: '0.875rem',
    },
}));

const HeaderActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.mode === 'dark'
        ? theme.palette.grey[400]
        : theme.palette.grey[700],
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.08)
            : alpha(theme.palette.common.black, 0.05),
        color: theme.palette.primary.main,
    },
}));

const LanguageButton = styled(Button)(({ theme }) => ({
    color: theme.palette.mode === 'dark'
        ? theme.palette.grey[400]
        : theme.palette.grey[700],
    padding: theme.spacing(0.5, 1.5),
    borderRadius: theme.spacing(2),
    textTransform: 'none',
    fontSize: '0.875rem',
    minWidth: '90px',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.08)
            : alpha(theme.palette.common.black, 0.05),
    },
}));

const LanguageMenuItem = styled(MenuItem)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1, 2),
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.common.white, 0.08)
            : alpha(theme.palette.common.black, 0.05),
    },
}));

const LanguageFlag = styled('img')(({ theme }) => ({
    width: '20px',
    height: '15px',
    borderRadius: '2px',
    objectFit: 'cover',
    boxShadow: `0 0 0 1px ${alpha(theme.palette.common.black, 0.1)}`,
}));

const EnglishFlag = () => (
    <LanguageFlag 
        src="/images/flags/en.png" 
        alt="English Flag"
        loading="lazy"
    />
);

const ArabicFlag = () => (
    <LanguageFlag 
        src="/images/flags/om.png" 
        alt="Oman Flag"
        loading="lazy"
    />
);

export const GuardianLayout: React.FC = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [open, setOpen] = useState(!isMobile);
    const { mode, setMode } = useColorScheme();
    const [expandedGroup, setExpandedGroup] = useState<string | null>('main');
    const [languageAnchorEl, setLanguageAnchorEl] = useState<null | HTMLElement>(null);
    const [searchQuery, setSearchQuery] = useState('');

    const handleDrawerToggle = () => {
        setOpen(!open);
    };

    const handleGroupClick = (group: string) => {
        setExpandedGroup(expandedGroup === group ? null : group);
    };

    const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
        setLanguageAnchorEl(event.currentTarget);
    };

    const handleLanguageClose = () => {
        setLanguageAnchorEl(null);
    };

    const handleLanguageChange = (lang: string) => {
        i18n.changeLanguage(lang);
        document.dir = lang === 'ar' ? 'rtl' : 'ltr';
        handleLanguageClose();
    };

    const menuGroups = [
        {
            id: 'main',
            title: t('guardian.sidebar.mainMenu'),
            badge: 'New',
            items: [
                { 
                    text: t('guardian.sidebar.dashboard'), 
                    icon: <ViewQuiltIcon className="dashboard-icon" />, 
                    path: '/guardian/dashboard' 
                },
                { 
                    text: t('guardian.sidebar.schools'), 
                    icon: <BusinessIcon className="school-icon" />, 
                    path: '/guardian/schools' 
                },
            ],
        },
        {
            id: 'management',
            title: t('guardian.sidebar.management'),
            items: [
                { 
                    text: t('guardian.sidebar.students'), 
                    icon: <SupervisorAccountIcon className="students-icon" />, 
                    path: '/guardian/students' 
                },
                { 
                    text: t('guardian.sidebar.assignments'), 
                    icon: <DescriptionIcon className="assignments-icon" />, 
                    path: '/guardian/assignments' 
                },
                { 
                    text: t('guardian.sidebar.calendar'), 
                    icon: <EventIcon className="calendar-icon" />, 
                    path: '/guardian/calendar' 
                },
            ],
        },
        {
            id: 'settings',
            title: t('guardian.sidebar.settings'),
            items: [
                { 
                    text: t('guardian.sidebar.profile'), 
                    icon: <BadgeIcon />, 
                    path: '/guardian/profile' 
                },
                { 
                    text: t('guardian.sidebar.settings'), 
                    icon: <SettingsApplicationsIcon />, 
                    path: '/guardian/settings' 
                },
            ],
        },
    ];

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
            <StyledAppBar position="fixed" elevation={0}>
                <StyledToolbar>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 200 }}>
                        <MenuButton
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerToggle}
                            edge="start"
                        >
                            <MenuIcon />
                        </MenuButton>
                        <Typography variant="h6" noWrap component="div" sx={{ fontWeight: 600 }}>
                            {t('guardian.appName')}
                        </Typography>
                    </Box>

                    <SearchBar>
                        <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
                        <InputBase
                            placeholder={t('common.search')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            sx={{ width: '100%' }}
                        />
                    </SearchBar>

                    <HeaderActions>
                        <Tooltip title={t('common.help')}>
                            <ActionButton>
                                <HelpIcon />
                            </ActionButton>
                        </Tooltip>

                        <Tooltip title={t('common.language')}>
                            <div>
                                <LanguageButton
                                    onClick={handleLanguageClick}
                                    endIcon={<KeyboardArrowDownIcon />}
                                    startIcon={<LanguageIcon />}
                                >
                                    {i18n.language === 'en' ? 'English' : 'العربية'}
                                </LanguageButton>
                            </div>
                        </Tooltip>

                        <Menu
                            anchorEl={languageAnchorEl}
                            open={Boolean(languageAnchorEl)}
                            onClose={handleLanguageClose}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            PaperProps={{
                                elevation: 3,
                                sx: {
                                    mt: 1,
                                    borderRadius: 2,
                                    minWidth: 180,
                                }
                            }}
                        >
                            <LanguageMenuItem onClick={() => handleLanguageChange('en')}>
                                <EnglishFlag />
                                <Box>
                                    <Typography variant="body1">English</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        English
                                    </Typography>
                                </Box>
                            </LanguageMenuItem>
                            <LanguageMenuItem onClick={() => handleLanguageChange('ar')}>
                                <ArabicFlag />
                                <Box>
                                    <Typography variant="body1">العربية</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Arabic
                                    </Typography>
                                </Box>
                            </LanguageMenuItem>
                        </Menu>

                        <ThemeSwitch
                            checked={mode === 'dark'}
                            onChange={() => setMode(mode === 'dark' ? 'light' : 'dark')}
                            icon={<LightModeIcon />}
                            checkedIcon={<DarkModeIcon />}
                        />

                        <NotificationBadge badgeContent={4} color="error">
                            <ActionButton>
                                <NotificationsIcon />
                            </ActionButton>
                        </NotificationBadge>

                        <Tooltip title={t('guardian.sidebar.profile')}>
                            <ActionButton>
                                <Avatar
                                    sx={{
                                        width: 32,
                                        height: 32,
                                        bgcolor: 'primary.main',
                                        fontSize: '0.875rem',
                                    }}
                                >
                                    G
                                </Avatar>
                            </ActionButton>
                        </Tooltip>
                    </HeaderActions>
                </StyledToolbar>
            </StyledAppBar>

            <StyledDrawer
                variant="persistent"
                anchor="left"
                open={open}
                onClose={handleDrawerToggle}
            >
                <SidebarHeader>
                    <LogoContainer>
                        <LogoIcon>
                            <SchoolIcon />
                        </LogoIcon>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                            {t('guardian.appName')}
                        </Typography>
                    </LogoContainer>
                </SidebarHeader>

                <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 80px)' }}>
                    {menuGroups.map((group) => (
                        <MenuSection key={group.id}>
                            <MenuGroupTitle>
                                <Typography>{group.title}</Typography>
                                {group.badge && (
                                    <Chip 
                                        label={group.badge}
                                        size="small"
                                        sx={{ 
                                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                            color: theme.palette.primary.main,
                                            fontWeight: 600,
                                            '& .MuiChip-label': {
                                                px: 1,
                                            },
                                        }}
                                    />
                                )}
                            </MenuGroupTitle>
                            <List disablePadding>
                                {group.items.map((item) => (
                                    <NavItem
                                        key={item.text}
                                        selected={location.pathname === item.path}
                                        onClick={() => navigate(item.path)}
                                    >
                                        <NavIcon>{item.icon}</NavIcon>
                                        <NavText primary={item.text} />
                                    </NavItem>
                                ))}
                            </List>
                        </MenuSection>
                    ))}

                    <UserSection>
                        <UserProfile>
                            <UserAvatar>G</UserAvatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="subtitle2" fontWeight="600" noWrap>
                                    Guardian Name
                                </Typography>
                                <Typography variant="caption" color="textSecondary" noWrap>
                                    guardian@email.com
                                </Typography>
                            </Box>
                            <Tooltip title={t('guardian.logout')}>
                                <IconButton 
                                    size="small"
                                    sx={{ 
                                        color: 'text.secondary',
                                        '&:hover': { 
                                            color: 'error.main',
                                            backgroundColor: alpha(theme.palette.error.main, 0.1),
                                        } 
                                    }}
                                >
                                    <PowerSettingsNewOutlinedIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </UserProfile>
                    </UserSection>
                </Box>
            </StyledDrawer>

            <Main open={open}>
                <Box sx={{ mt: 10, p: 3 }}>
                    <Outlet />
                </Box>
            </Main>
        </Box>
    );
}; 